import type { ChargeSummaryExtended, ChargingAgingResponse, DashboardInsight } from '@yawi/types';

/**
 * El backend prioriza `mora_riesgo` > `occupancy_drop` > `budget_overrun`. Cuando devuelve
 * `occupancy_drop`, ese insight no es relevante para el admin. Esta función deriva un insight
 * local más útil a partir de la mora y la cobranza ya cargadas en el dashboard.
 * Si no hay nada relevante, retorna `null` y el card muestra "Todo en orden".
 */
export function deriveLocalInsight(
  aging: ChargingAgingResponse | null,
  summary: ChargeSummaryExtended | null,
): DashboardInsight | null {
  // 1. Mora pendiente
  if (aging && aging.totalAmount > 0 && aging.totalUnits > 0) {
    return {
      type: 'mora_riesgo',
      headline: `${aging.totalUnits} ${aging.totalUnits === 1 ? 'unidad' : 'unidades'} con saldo pendiente`,
      unitsCount: aging.totalUnits,
      metricLabel: 'unidades con saldo',
      potentialLoss: { amount: aging.totalAmount, label: 'en mora acumulada' },
      primaryCta: { label: 'Ver cobranza', action: '/admin/cobranza?filter=overdue' },
      secondaryCta: null,
    };
  }

  // 2. Cobranza baja
  if (summary && summary.targetAmount > 0) {
    const pct = Math.round((summary.amountPaid / summary.targetAmount) * 100);
    if (pct < 80) {
      return {
        type: 'budget_overrun',
        headline: `Cobranza del mes en ${pct}%`,
        unitsCount: summary.unitsPending,
        metricLabel: 'por debajo del objetivo de cobranza',
        potentialLoss: { amount: summary.amountPending, label: 'por cobrar este mes' },
        primaryCta: { label: 'Ver cobranza', action: '/admin/cobranza' },
        secondaryCta: null,
      };
    }
  }

  // 3. Nada relevante
  return null;
}
